"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

import {
  matchJobRoute,
  type JobTabItem,
} from "@/features/jobs/components/view/layout/job-tab-items";
import { useVisibleJobTabItems } from "@/features/jobs/components/view/layout/use-visible-job-tab-items";
import { matchSequenceRoute } from "@/features/sequences/components/detail/sequence-tab-items";
import { useSequence } from "@/features/sequences/hooks/use-sequence";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/stores/sidebar-store";

import { PanelBackButton } from "./panel-back-button";

function JobPanelRow({
  item,
  href,
  active,
  onActiveClick,
}: {
  item: JobTabItem;
  href: string;
  active: boolean;
  onActiveClick: () => void;
}) {
  // Coming-soon tabs (Sourcing Agent) render non-interactively, mirroring
  // the on-page strip's disabled treatment (`navigation-tabs.tsx`).
  if (item.comingSoon) {
    return (
      <div
        className="flex h-8 w-full cursor-not-allowed select-none items-center gap-2 rounded-md px-2 opacity-50"
        aria-disabled="true"
      >
        <Icon
          name={item.icon}
          size={16}
          className="size-4 shrink-0 text-contrast opacity-[0.45]"
        />
        <span className="flex-1 truncate text-sm font-medium leading-5 text-contrast/50">
          {item.label}
        </span>
      </div>
    );
  }

  return (
    <Link
      href={href}
      aria-current={active ? "page" : undefined}
      onClick={active ? onActiveClick : undefined}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-md px-2 transition-colors",
        active
          ? "sidebar-active text-heading"
          : "text-contrast/65 hover:bg-contrast/4 hover:text-heading",
      )}
    >
      <Icon
        name={item.icon}
        size={16}
        className={cn(
          "size-4 shrink-0 text-contrast",
          active ? "opacity-90" : "opacity-[0.45]",
        )}
      />
      <span className="flex-1 truncate text-sm font-medium leading-5">
        {item.label}
      </span>
    </Link>
  );
}

export function JobPanel({
  onBack,
  onSequenceDrillIn,
}: {
  onBack: () => void;
  onSequenceDrillIn: () => void;
}) {
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();
  const tabItems = useVisibleJobTabItems();
  const match = matchJobRoute(pathname);
  // On a sequence-detail route the panel's header row gains a right-aligned
  // "«sequence name» ›" drill button (next to Back) that drills one level
  // deeper into the `SequencePanel`.
  const sequenceMatch = matchSequenceRoute(pathname);
  // Same hook as the sequence detail page rendered below the drawer, so
  // this is a cache hit — no extra fetch on drawer open. Called
  // unconditionally to satisfy the rules of hooks; `useSequence` disables
  // the query itself when the ids are null (non-sequence routes).
  const { sequence } = useSequence({
    jobId: sequenceMatch?.jobId ?? null,
    sequenceId: sequenceMatch?.sequenceId ?? null,
  });

  // Only reachable via the contextual job drill row in `main-content.tsx`
  // (or the contextual initial-panel resolution), both gated on a matching
  // route — so this should be unreachable in practice. Guard anyway rather
  // than crash on a null jobId.
  if (!match) return null;

  const base = `/jobs/${match.jobId}`;
  const sequenceName = sequence?.name ?? "Sequence";

  return (
    <nav aria-label="Job" className="flex flex-col gap-0.5 px-3.5 py-2.5">
      <div className="flex h-8 w-full items-center justify-between gap-1">
        <PanelBackButton onBack={onBack} />
        {sequenceMatch && (
          <button
            type="button"
            onClick={onSequenceDrillIn}
            className="flex h-8 min-w-0 max-w-[65%] cursor-pointer items-center gap-1 rounded-md px-1.5 text-contrast/65 transition-colors hover:bg-contrast/4 hover:text-heading"
          >
            <span className="min-w-0 truncate text-sm font-medium leading-5">
              {sequenceName}
            </span>
            <Icon
              name="IconChevronRight"
              size={16}
              className="shrink-0 text-contrast opacity-40"
            />
          </button>
        )}
      </div>

      {tabItems.map((item) => {
        const href = `${base}/${item.value}`;
        const active =
          !item.comingSoon && (pathname?.startsWith(href) ?? false);
        return (
          <JobPanelRow
            key={item.value}
            item={item}
            href={href}
            active={active}
            onActiveClick={() => setOpenMobile(false)}
          />
        );
      })}
    </nav>
  );
}
